/**
 * 登录 / 注册页（复用 aibox 两栏落地页布局：左品牌介绍、右登录框）。
 * 字段与流程沿用旧版（用户名+密码+授权码登录；手机/邮箱+验证码注册），接口走 V2 /api/v2/auth/*。
 * 视觉统一到新 Pro 系统（蓝紫、点阵背景、发光输入框、真实 logo）。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, MapPin, Building2 } from 'lucide-react';

import { authApi } from '../authApi';

const field = 'w-full rounded-control bg-surface-base px-3.5 py-3 text-ui text-ink-primary outline-none border border-surface-line focus:border-accent';
const label = 'mb-1 block text-uiSm text-ink-faint';

type Tab = 'login' | 'register';
type RegType = 'phone' | 'email';

export function LoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('login');

  // 登录
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [license, setLicense] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 注册
  const [regType, setRegType] = useState<RegType>('phone');
  const [rUser, setRUser] = useState('');
  const [rPwd, setRPwd] = useState('');
  const [rConfirm, setRConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [company, setCompany] = useState('');
  const [city, setCity] = useState('');
  const [rLoading, setRLoading] = useState(false);
  const [rMsg, setRMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [countdown, setCountdown] = useState(0);

  async function doLogin(): Promise<void> {
    if (username.trim() === '' || password === '') {
      setError('请输入用户名和密码');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const d = await authApi.login(username.trim(), password, license.trim() || undefined);
      if (d.status === 'success') {
        navigate('/');
      } else {
        setError(d.message ?? '登录失败');
      }
    } catch {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }

  function startCountdown(): void {
    setCountdown(60);
    const t = setInterval(() => setCountdown((c) => (c <= 1 ? (clearInterval(t), 0) : c - 1)), 1000);
  }

  async function doSendCode(): Promise<void> {
    if (regType === 'phone') {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        setRMsg({ text: '请输入正确的手机号', ok: false });
        return;
      }
      const d = await authApi.sendSmsCode(phone).catch(() => ({ status: 'error', message: '网络错误' }));
      if (d.status === 'success') {
        startCountdown();
        setRMsg({ text: `验证码已发送至 ${phone}`, ok: true });
      } else {
        setRMsg({ text: d.message ?? '发送失败', ok: false });
      }
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setRMsg({ text: '请输入正确的邮箱', ok: false });
        return;
      }
      const d = await authApi.sendEmailCode(email).catch(() => ({ status: 'error', message: '网络错误' }));
      if (d.status === 'success') {
        startCountdown();
        setRMsg({ text: `验证码已发送至 ${email}`, ok: true });
      } else {
        setRMsg({ text: d.message ?? '发送失败', ok: false });
      }
    }
  }

  async function doRegister(): Promise<void> {
    if (rUser.trim().length < 3) {
      setRMsg({ text: '用户名至少 3 个字符', ok: false });
      return;
    }
    if (rPwd.length < 6) {
      setRMsg({ text: '密码至少 6 位', ok: false });
      return;
    }
    if (rPwd !== rConfirm) {
      setRMsg({ text: '两次密码不一致', ok: false });
      return;
    }
    if (!code) {
      setRMsg({ text: '请先获取并填写验证码', ok: false });
      return;
    }
    setRLoading(true);
    const payload = { username: rUser.trim(), password: rPwd, verification_code: code, company: company.trim(), city: city.trim() };
    try {
      const d = regType === 'phone'
        ? await authApi.selfRegister({ ...payload, phone })
        : await authApi.selfRegisterEmail({ ...payload, email });
      if (d.status === 'success') {
        setRMsg({ text: '注册成功，请登录', ok: true });
        setTab('login');
      } else {
        setRMsg({ text: d.message ?? '注册失败', ok: false });
      }
    } catch {
      setRMsg({ text: '网络错误，请稍后重试', ok: false });
    } finally {
      setRLoading(false);
    }
  }

  const segBtn = (on: boolean): string => `flex-1 rounded-control px-3 py-2 text-ui transition-colors ${on ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-secondary hover:text-ink-primary'}`;

  return (
    <div className="relative h-full overflow-hidden bg-surface-base">
      {/* 整页背景：发光视频铺满（不再用点阵黑底） */}
      <video src="/videos/login-glow.mp4" autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" />

      {/* 叠加内容：主标题在上 + 登录框在下，整体偏右但已左移（右边距加倍） */}
      <div className="relative z-10 flex h-full items-center justify-center px-6 lg:justify-end lg:pr-40">
        <div className="w-full max-w-md">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="logo" className="h-24 w-24 rounded-3xl" />
          <h1 className="title-shimmer text-4xl font-bold tracking-tight">智创短剧创作平台 Pro</h1>
          <p className="text-lg leading-relaxed text-ink-muted">节点式自由画布 · 从剧本到成片的一站式 AI 短剧创作工作台</p>
        </div>
        <div className="agent-glow relative w-full max-w-md rounded-card border border-surface-edge bg-surface-overlay/90 p-8 backdrop-blur-sm">

          {/* tab */}
          <div className="mb-5 flex gap-1 rounded-control bg-surface-base p-1">
            <button type="button" onClick={() => setTab('login')} className={segBtn(tab === 'login')}>登录</button>
            <button type="button" onClick={() => setTab('register')} className={segBtn(tab === 'register')}>注册</button>
          </div>

          {tab === 'login' && (
            <div className="flex flex-col gap-3">
              <div>
                <span className={label}>用户名</span>
                <input value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void doLogin()} className={field} placeholder="用户名 / 手机号" autoComplete="username" />
              </div>
              <div>
                <span className={label}>密码</span>
                <div className="relative">
                  <input type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void doLogin()} className={`${field} pr-10`} placeholder="请输入密码" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label="显示密码" className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-primary">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <span className={label}>授权码（可选，团队/授权版填写）</span>
                <input value={license} onChange={(e) => setLicense(e.target.value)} className={field} placeholder="留空则普通登录" />
              </div>
              {error !== '' && <p className="rounded-control bg-state-danger/10 px-2.5 py-1.5 text-uiSm text-state-danger">{error}</p>}
              <button type="button" onClick={() => void doLogin()} disabled={loading} className="btn-primary mt-1 inline-flex items-center justify-center gap-1.5 py-2.5 disabled:opacity-50">
                {loading && <Loader2 size={15} className="animate-spin" />}
                登录
              </button>
              <p className="text-center text-uiSm text-ink-faint">
                还没有账号？
                <button type="button" onClick={() => setTab('register')} className="ml-1 text-accent hover:underline">立即注册</button>
              </p>
            </div>
          )}

          {tab === 'register' && (
            <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-0.5">
              <div className="flex gap-1 rounded-control bg-surface-base p-1">
                <button type="button" onClick={() => { setRegType('phone'); }} className={segBtn(regType === 'phone')}>手机号注册</button>
                <button type="button" onClick={() => { setRegType('email'); }} className={segBtn(regType === 'email')}>邮箱注册</button>
              </div>
              <div>
                <span className={label}>用户名 *</span>
                <input value={rUser} onChange={(e) => setRUser(e.target.value)} className={field} placeholder="3–20 个字符" maxLength={20} />
              </div>
              <div>
                <span className={label}>密码 *</span>
                <input type="password" value={rPwd} onChange={(e) => setRPwd(e.target.value)} className={field} placeholder="至少 6 位" />
              </div>
              <div>
                <span className={label}>确认密码 *</span>
                <input type="password" value={rConfirm} onChange={(e) => setRConfirm(e.target.value)} className={field} placeholder="再次输入密码" />
              </div>
              {regType === 'phone' ? (
                <div>
                  <span className={label}>手机号 *</span>
                  <div className="flex gap-2">
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className={`${field} flex-1`} placeholder="11 位手机号" maxLength={11} />
                    <button type="button" onClick={() => void doSendCode()} disabled={countdown > 0} className="btn-ghost shrink-0 px-3 text-uiSm disabled:opacity-50">{countdown > 0 ? `${countdown}s` : '获取验证码'}</button>
                  </div>
                </div>
              ) : (
                <div>
                  <span className={label}>邮箱 *</span>
                  <div className="flex gap-2">
                    <input value={email} onChange={(e) => setEmail(e.target.value)} className={`${field} flex-1`} placeholder="you@example.com" />
                    <button type="button" onClick={() => void doSendCode()} disabled={countdown > 0} className="btn-ghost shrink-0 px-3 text-uiSm disabled:opacity-50">{countdown > 0 ? `${countdown}s` : '获取验证码'}</button>
                  </div>
                </div>
              )}
              <div>
                <span className={label}>验证码 *</span>
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={field} placeholder="6 位验证码" maxLength={6} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className={label}><Building2 size={11} className="mr-1 inline" />公司（选填）</span>
                  <input value={company} onChange={(e) => setCompany(e.target.value)} className={field} placeholder="公司" />
                </div>
                <div>
                  <span className={label}><MapPin size={11} className="mr-1 inline" />城市（选填）</span>
                  <input value={city} onChange={(e) => setCity(e.target.value)} className={field} placeholder="城市" />
                </div>
              </div>
              {rMsg !== null && <p className={`rounded-control px-2.5 py-1.5 text-uiSm ${rMsg.ok ? 'bg-state-ok/10 text-state-ok' : 'bg-state-danger/10 text-state-danger'}`}>{rMsg.text}</p>}
              <button type="button" onClick={() => void doRegister()} disabled={rLoading} className="btn-primary mt-1 inline-flex items-center justify-center gap-1.5 py-2.5 disabled:opacity-50">
                {rLoading && <Loader2 size={15} className="animate-spin" />}
                注册
              </button>
              <p className="text-center text-uiSm text-ink-faint">
                已有账号？
                <button type="button" onClick={() => setTab('login')} className="ml-1 text-accent hover:underline">去登录</button>
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
      {/* 页脚备案信息 */}
      <div className="absolute bottom-3 left-0 right-0 z-10 text-center text-uiSm text-ink-faint">© 智创短剧PRO · aisom.cn · 琼ICP备2026013295号-3</div>
    </div>
  );
}
